// src/prototype/pages/EAROIModelPage.tsx
// ============================================================
// EA ROI MODEL PAGE
//
// Visual chrome matches the Decision Brief (sticky rank bar, identity header,
// StatTiles). Scenario lab, sensitivity, waterfall, and CSV export stay API-backed.
//
// Route: /ea/roi-model/:id
// ============================================================

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  HelpCircle,
  Activity,
  CheckCircle2,
  Edit2,
  Sparkles,
  ArrowUpRight,
  Info,
  Target,
  DollarSign,
  Settings,
  Users,
  Database,
  Lightbulb,
  Loader2,
  Share2,
  BarChart3,
  Award,
  Clock,
  FileText,
  LineChart,
} from 'lucide-react';
import { EAJourneyBar } from '../components/EAJourneyBar';
import { HelpPopover } from '../components/help/HelpPopover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { StatTile } from '../components/StatTile';
import { EffortTagPill, type EffortTag } from '../components/EffortTagPill';
import type { RestoreState } from '../components/RelatedViewsToolbar';
import { EA_CLIENT_ID } from '../constants/eaClient';
import {
  ensureCatalogBriefSnapshot,
  fetchRoiModel,
  fetchOpportunity,
  fetchOpportunities,
  isPlaceholderUseCaseTitle,
  peekCatalogBriefSnapshot,
  peekRoiModel,
  exportRoiModelCsv,
  saveCustomAssumptions,
  UCE_CATALOG_FILTERS,
  type UCEOpportunity,
} from '../services/useCaseExplorerService';
import { fetchScoringRationale } from '../services/scoringRationaleService';
import { buildDrilldownNav, readDrilldownNav, resolveDrilldownExit } from '../utils/eaDrilldownNav';
import type { ROIModelResponse } from '@/services/roiService';
import {
  buildAssumptionGroupsFromAssumptionLab,
  buildSensitivityRankingFromLab,
  collectCustomAssumptionOverrides,
  formatHeaderRoiPercent,
  formatCurrency,
  narrativeFromScenarioDetails,
  detailsForScenario,
  buildCustomScenarioNarrative,
  formatAssumptionDisplayValue,
  pickFinancialBreakdownForScenario,
  resolveCustomScenario,
} from '@/services/roiService';

// ============================================================================
// TYPES
// ============================================================================

type ScenarioId = 'conservative' | 'base' | 'aggressive' | 'custom';
type ConfidenceLevel = 'High' | 'Medium' | 'Low';

interface ROIScenario {
  id: ScenarioId;
  name: string;
  sublabel: string;
  description: string;
  roi: number;
  paybackPeriod: number;
  netValue3Year: number;
  confidence: ConfidenceLevel;
  modifiedCount?: number;
  valueDrivers: string[];
  keyAssumptions: string[];
  risks: string[];
}

interface Assumption {
  id: string;
  name: string;
  description?: string;
  conservative: number;
  base: number;
  aggressive: number;
  /** API-scale value (matches assumptionLab). */
  custom: number;
  baseRaw?: number;
  unit: string;
  confidence: ConfidenceLevel;
  source: string;
  sourceType: 'External Benchmark' | 'Historical' | 'Estimate';
  impact: number;
  changed?: boolean;
}

interface AssumptionGroup {
  id: string;
  name: string;
  icon: string;
  expanded?: boolean;
  assumptions: Assumption[];
}

interface SensitivityItem {
  rank: number;
  name: string;
  label: string;
  assumptionKey?: string;
  positive: number;
  negative: number;
  impactExplanation: string;
}

interface RoiPageData {
  title: string;
  subtitle: string;
  roiBase: number;
  paybackPeriod: number | null;
  netValue3Year: number;
  confidence: ConfidenceLevel;
  aiInsight: string;
}

// ============================================================================
// Scenario copy — API scenarioDetails fills the active scenario's lists
// ============================================================================

const EA_SCENARIO_META: Record<
  string,
  Pick<ROIScenario, 'name' | 'sublabel' | 'description' | 'valueDrivers' | 'keyAssumptions' | 'risks'>
> = {
  conservative: {
    name: 'Conservative',
    sublabel: 'Standard Model',
    description: 'Lower-bound estimates using cautious assumptions.',
    valueDrivers: ['Conservative financial and adoption parameters from the model'],
    keyAssumptions: ['Uses conservative branch of each assumption in the lab'],
    risks: ['Slower adoption or delayed benefits compress upside'],
  },
  base: {
    name: 'Base Case',
    sublabel: 'Standard Model',
    description: 'Most likely outcome based on benchmarks and comparable implementations.',
    valueDrivers: ['Baseline benefits, costs, and net value per API scenario card'],
    keyAssumptions: ['Central case inputs from the assumption laboratory'],
    risks: ['Execution and vendor dependencies as in comparable programs'],
  },
  aggressive: {
    name: 'Aggressive',
    sublabel: 'Standard Model',
    description: 'Upside case when adoption and efficiency outperform base.',
    valueDrivers: ['Accelerated value capture in the aggressive scenario card'],
    keyAssumptions: ['Aggressive branch of lab assumptions'],
    risks: ['Stretch timelines and higher change-management demand'],
  },
  custom: {
    name: 'Custom',
    sublabel: 'User Modified',
    description: 'Edit assumptions below to reflect your operating plan.',
    valueDrivers: ['Reflects custom values you enter in the assumption lab'],
    keyAssumptions: ['Custom column tracks your overrides'],
    risks: ['Validate custom inputs against internal benchmarks'],
  },
};

// ============================================================================
// ICON MAPPING & HELPERS
// ============================================================================

const TOTAL_PORTFOLIO_SIZE = 175;

const EFFORT_TO_TAG: Record<UCEOpportunity['effort_tier'], EffortTag> = {
  Low: 'Quick Win',
  Medium: 'Moderate',
  High: 'High',
};

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = { DollarSign, Settings, Users, Target };

const Badge: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${className}`}>{children}</span>
);

// Simple Waterfall Chart Component
const WaterfallChart: React.FC<{ financial: { baseline: number; benefits: number; costs: number; net_value: number } }> = ({ financial }) => {
  const fmt = (value: number) => {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${value < 0 ? '-' : ''}$${(abs / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `${value < 0 ? '-' : ''}$${(abs / 1_000).toFixed(0)}K`;
    return `${value < 0 ? '-' : ''}$${abs.toFixed(0)}`;
  };

  const bars = [
    { name: 'Baseline', value: Math.abs(financial.baseline), displayValue: fmt(financial.baseline), color: '#e4e4e7' },
    { name: 'Benefits', value: Math.abs(financial.benefits), displayValue: fmt(financial.benefits), color: '#18365E' },
    { name: 'Costs', value: Math.abs(financial.costs), displayValue: fmt(financial.costs), color: '#ef4444' },
    { name: 'Net Value', value: Math.abs(financial.net_value), displayValue: fmt(financial.net_value), color: '#FF6E4C' },
  ];

  const maxValue = Math.max(...bars.map((item) => item.value), 1);

  return (
    <div className="space-y-3">
      {bars.map((item) => (
        <div key={item.name} className="flex items-center gap-3">
          <div className="w-24 text-[10px] font-medium text-[#52525b] text-right">{item.name}</div>
          <div className="flex-1 h-8 bg-[#f5f5f5] rounded overflow-hidden">
            <div
              className="h-full rounded transition-all"
              style={{
                width: `${(item.value / maxValue) * 100}%`,
                backgroundColor: item.color,
              }}
            />
          </div>
          <div className="w-[4.5rem] text-right text-[11px] font-bold text-[#18365E] tabular-nums">
            {item.displayValue}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const EAROIModelPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  // Support both :id and :opportunityId route parameter names
  const params = useParams<{ id?: string; opportunityId?: string }>();
  const useCaseId = params.id || params.opportunityId || '';
  const location = useLocation();
  const navigate = useNavigate();
  const clientId = EA_CLIENT_ID;

  const navState = readDrilldownNav(location.state);
  const restoreState: RestoreState | undefined = navState.restore;
  // DMD-1186: ← back always exits to All Opportunities / Execution — not sibling tabs.
  const exitNav = resolveDrilldownExit(navState);
  const fromStep3 = exitNav.fromActivate;
  const navSnapshot =
    navState.briefSnapshot && String(navState.briefSnapshot.id) === String(useCaseId)
      ? navState.briefSnapshot
      : null;

  // State
  const cachedRoi = useCaseId ? peekRoiModel(useCaseId) : null;
  const [activeScenario, setActiveScenario] = useState<ScenarioId>('base');
  const [roiResponse, setRoiResponse] = useState<ROIModelResponse | null>(() => cachedRoi);
  const [loading, setLoading] = useState(() => !cachedRoi);
  const [error, setError] = useState<string | null>(null);
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const [exportCsvError, setExportCsvError] = useState<string | null>(null);
  const [assumptionGroups, setAssumptionGroups] = useState<AssumptionGroup[]>(() =>
    cachedRoi ? buildAssumptionGroupsFromAssumptionLab(cachedRoi.data.assumptionLab) : [],
  );
  const [highlightedAssumptionId, setHighlightedAssumptionId] = useState<string | null>(null);
  const [customSaveStatus, setCustomSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const assumptionsLabRef = useRef<HTMLDivElement>(null);
  const customSaveTimerRef = useRef<number | null>(null);
  const lastSavedCustomJsonRef = useRef(
    cachedRoi
      ? JSON.stringify(
          collectCustomAssumptionOverrides(
            buildAssumptionGroupsFromAssumptionLab(cachedRoi.data.assumptionLab),
          ),
        )
      : '',
  );
  const pendingCustomJsonRef = useRef<string | null>(null);
  const [opportunity, setOpportunity] = useState<UCEOpportunity | null>(null);
  const [snapshotTitle, setSnapshotTitle] = useState<string | null>(
    () => navSnapshot?.title ?? peekCatalogBriefSnapshot(clientId, useCaseId)?.title ?? null,
  );
  const [rankedIds, setRankedIds] = useState<string[]>(() =>
    Array.isArray(navState.rankedIds) ? navState.rankedIds.map(String) : [],
  );
  const [catalogTotal, setCatalogTotal] = useState(() =>
    typeof navState.catalogTotal === 'number' ? navState.catalogTotal : TOTAL_PORTFOLIO_SIZE,
  );

  useEffect(() => {
    if (embedded) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [useCaseId, embedded]);

  useEffect(() => {
    const seeded =
      [navSnapshot?.title, peekCatalogBriefSnapshot(clientId, useCaseId)?.title].find(
        (t) => t && !isPlaceholderUseCaseTitle(t),
      ) ?? null;
    setSnapshotTitle(seeded);
    if (seeded || !clientId || !useCaseId) return;
    let cancelled = false;
    void ensureCatalogBriefSnapshot(clientId, useCaseId).then((snap) => {
      if (cancelled || !snap?.title) return;
      setSnapshotTitle((prev) =>
        !prev || isPlaceholderUseCaseTitle(prev) ? snap.title : prev,
      );
    });
    return () => {
      cancelled = true;
    };
  }, [clientId, useCaseId, navSnapshot?.title]);

  // Opportunity is optional chrome — always resolve when title is missing so prev/next
  // never sticks on "Use Case N" after the 60s catalog TTL (DMD-1180).
  useEffect(() => {
    if (!clientId || !useCaseId) return;
    if (snapshotTitle && !isPlaceholderUseCaseTitle(snapshotTitle)) return;
    let cancelled = false;
    fetchOpportunity(clientId, useCaseId)
      .then((op) => {
        if (cancelled || !op) return;
        setOpportunity(op);
        const name = String(op.name ?? op.title ?? '').trim();
        if (name && !isPlaceholderUseCaseTitle(name)) {
          setSnapshotTitle((prev) =>
            !prev || isPlaceholderUseCaseTitle(prev) ? name : prev,
          );
        }
      })
      .catch(() => {
        if (!cancelled) setOpportunity(null);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, useCaseId, snapshotTitle]);

  useEffect(() => {
    if (!clientId) return;
    if (rankedIds.length > 0) return;
    let cancelled = false;
    fetchOpportunities(String(clientId), UCE_CATALOG_FILTERS)
      .then((res) => {
        if (cancelled) return;
        setRankedIds((res.data ?? []).map((o) => String(o.use_case_id)));
        setCatalogTotal(res.pagination?.total || res.data?.length || TOTAL_PORTFOLIO_SIZE);
      })
      .catch(() => {
        if (!cancelled) setRankedIds([]);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, rankedIds.length]);

  useEffect(() => {
    if (!clientId || !useCaseId) return;

    const applyRoi = (res: ROIModelResponse) => {
      setRoiResponse(res);
      const groups = buildAssumptionGroupsFromAssumptionLab(res.data.assumptionLab);
      setAssumptionGroups(groups);
      lastSavedCustomJsonRef.current = JSON.stringify(collectCustomAssumptionOverrides(groups));
      pendingCustomJsonRef.current = null;
      setCustomSaveStatus('idle');
      setError(null);
      setLoading(false);
      void fetchScoringRationale(String(clientId), String(useCaseId));
    };

    const cached = peekRoiModel(useCaseId);
    if (cached) {
      applyRoi(cached);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchRoiModel(String(clientId), useCaseId)
      .then((res) => {
        if (cancelled) return;
        applyRoi(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setRoiResponse(null);
        setAssumptionGroups([]);
        setError(err instanceof Error ? err.message : 'Failed to load ROI model');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, useCaseId]);

  // Derived data
  const data: RoiPageData = useMemo(() => {
    const fromSnap =
      snapshotTitle && !isPlaceholderUseCaseTitle(snapshotTitle) ? snapshotTitle : null;
    const fromOpp = String(opportunity?.name ?? opportunity?.title ?? '').trim();
    const oppOk = fromOpp && !isPlaceholderUseCaseTitle(fromOpp) ? fromOpp : null;
    const fromCatalog = peekCatalogBriefSnapshot(clientId, useCaseId)?.title;
    const catalogOk =
      fromCatalog && !isPlaceholderUseCaseTitle(fromCatalog) ? fromCatalog : null;
    const title =
      fromSnap || oppOk || catalogOk || (useCaseId ? `Use Case ${useCaseId}` : 'Use Case');
    const subtitle = 'Validate assumptions, model scenarios, run sensitivity analysis, and finalize the ROI story.';
    if (roiResponse?.data?.headerMetrics) {
      const h = roiResponse.data.headerMetrics;
      return {
        title,
        subtitle,
        roiBase: h.roiBase,
        paybackPeriod: h.paybackMonthsBase,
        netValue3Year: h.net3YearValueBase,
        confidence: h.confidenceBase as ConfidenceLevel,
        aiInsight: roiResponse.data.aiInsights,
      };
    }
    if (loading) {
      return {
        title,
        subtitle: '',
        roiBase: 0,
        paybackPeriod: null,
        netValue3Year: 0,
        confidence: 'Medium' as ConfidenceLevel,
        aiInsight: '',
      };
    }
    return {
      title,
      subtitle,
      roiBase: 0,
      paybackPeriod: null,
      netValue3Year: 0,
      confidence: 'Medium' as ConfidenceLevel,
      aiInsight: '',
    };
  }, [roiResponse, useCaseId, loading, opportunity, snapshotTitle]);

  // Custom column values drive the Custom card, waterfall, and Modified badge, so every
  // edit in the Assumptions Laboratory recalculates instead of echoing Base (DMD-1106).
  const customOverrides = useMemo(
    () => collectCustomAssumptionOverrides(assumptionGroups),
    [assumptionGroups],
  );

  const customScenario = useMemo(
    () => resolveCustomScenario(roiResponse?.data, customOverrides),
    [roiResponse, customOverrides],
  );

  const scenarios = useMemo((): ROIScenario[] => {
    if (!roiResponse?.data?.scenarioCards) return [];
    const { scenarioCards } = roiResponse.data;

    const core = (['conservative', 'base', 'aggressive'] as const).map((id) => {
      const card = scenarioCards[id];
      const m = EA_SCENARIO_META[id];
      const narrative = narrativeFromScenarioDetails(
        detailsForScenario(roiResponse.data, id),
        m,
      );
      return {
        id,
        name: m.name,
        sublabel: m.sublabel,
        ...narrative,
        roi: card.roi,
        paybackPeriod: card.paybackMonths,
        netValue3Year: card.net3YearValue / 1_000_000,
        confidence: card.confidence,
      };
    });

    const customCard = customScenario?.card ?? scenarioCards.base;
    const m = EA_SCENARIO_META.custom;
    // DMD-1182: rebuild Custom narrative from overrides + projected card so detail
    // panel matches the summary (API custom details often echo Base).
    const customNarrative = buildCustomScenarioNarrative(
      roiResponse.data,
      customOverrides,
      customCard,
      m,
    );
    core.push({
      id: 'custom',
      name: m.name,
      sublabel: m.sublabel,
      ...customNarrative,
      roi: customCard.roi,
      paybackPeriod: customCard.paybackMonths,
      netValue3Year: customCard.net3YearValue / 1_000_000,
      confidence: customCard.confidence,
      modifiedCount: customScenario?.modifiedCount ?? 0,
    });

    return core;
  }, [roiResponse, customScenario, customOverrides]);

  const selectedScenario =
    scenarios.find((s) => s.id === activeScenario) ||
    scenarios.find((s) => s.id === 'base') ||
    scenarios[0];

  const baseScenario = scenarios.find((s) => s.id === 'base') || scenarios[0];

  const mappedSensitivityData: SensitivityItem[] = useMemo(() => {
    if (!roiResponse?.data?.assumptionLab || Object.keys(roiResponse.data.assumptionLab).length === 0) {
      return [];
    }
    // DMD-1184: rank by true ±20% Δ Net Value around current Custom overrides.
    return buildSensitivityRankingFromLab(roiResponse.data, customOverrides);
  }, [roiResponse, customOverrides]);

  const selectedFinancial = useMemo(() => {
    const f = activeScenario === 'custom' && customScenario
      ? customScenario.financialBreakdown
      : pickFinancialBreakdownForScenario(roiResponse?.data, activeScenario);
    return {
      baseline: f.baseline,
      benefits: f.benefit,
      costs: f.costs,
      net_value: f.netvalue,
    };
  }, [roiResponse, activeScenario, customScenario]);

  // DMD-1186: primary back always exits the drilldown; tabs handle Brief ↔ Rationale ↔ ROI.
  const backLabel = exitNav.label;
  const backPath = exitNav.path;

  const rank = opportunity?.rank ?? 0;
  const portfolioSize = catalogTotal || TOTAL_PORTFOLIO_SIZE;
  const idx = rankedIds.indexOf(String(useCaseId || ''));
  const prevId = idx > 0 ? rankedIds[idx - 1] : null;
  const nextId = idx >= 0 && idx < rankedIds.length - 1 ? rankedIds[idx + 1] : null;
  const pageTo = (targetId: string | null) => {
    if (!targetId) return;
    void (async () => {
      const catalogSnap =
        peekCatalogBriefSnapshot(clientId, targetId) ||
        (await ensureCatalogBriefSnapshot(clientId, targetId));
      navigate(`/ea/use-case/${targetId}/roi`, {
        state: buildDrilldownNav(navState, {
          rankedIds,
          catalogTotal,
          briefSnapshot: catalogSnap,
        }),
      });
    })();
  };

  const drilldownState = buildDrilldownNav(navState, {
    rankedIds,
    catalogTotal,
    briefSnapshot: {
      id: String(useCaseId),
      title: data.title,
      category: opportunity?.category_name || opportunity?.category || navSnapshot?.category || '',
      totalScore: opportunity?.total_score,
      valueBand: opportunity?.value_band ? String(opportunity.value_band) : navSnapshot?.valueBand,
    },
  });
  const handleBack = () => {
    navigate(backPath, {
      state: {
        restore: restoreState,
        ...(fromStep3 ? {} : { returnToTab: 'explore' }),
      },
    });
  };
  const handleShare = () => {
    void navigator.clipboard?.writeText(window.location.href);
  };
  const categoryLabel = String(opportunity?.category_name ?? opportunity?.category ?? '').trim();
  const effortTag = opportunity?.effort_tier ? (EFFORT_TO_TAG[opportunity.effort_tier] ?? 'Moderate') : null;

  const showRoiLoader = !error && loading && !roiResponse;

  // Helpers
  const getDeltaFromBase = (value: number, baseValue: number) => {
    const delta = value - baseValue;
    return { delta: Math.abs(delta), isUp: delta > 0 };
  };

  const persistCustomAssumptions = useCallback((groups: AssumptionGroup[], immediate = false) => {
    if (!clientId || !useCaseId) return;
    const payload = collectCustomAssumptionOverrides(groups);
    const serialized = JSON.stringify(payload);
    pendingCustomJsonRef.current = serialized;
    if (serialized === lastSavedCustomJsonRef.current) {
      pendingCustomJsonRef.current = null;
      return;
    }

    const flush = () => {
      const next = pendingCustomJsonRef.current;
      if (next == null || next === lastSavedCustomJsonRef.current) return;
      pendingCustomJsonRef.current = null;
      setCustomSaveStatus('saving');
      saveCustomAssumptions(String(clientId), useCaseId, JSON.parse(next) as Record<string, number>)
        .then(() => {
          lastSavedCustomJsonRef.current = next;
          setCustomSaveStatus('saved');
        })
        .catch(() => {
          pendingCustomJsonRef.current = next;
          setCustomSaveStatus('error');
        });
    };

    if (customSaveTimerRef.current != null) {
      window.clearTimeout(customSaveTimerRef.current);
      customSaveTimerRef.current = null;
    }
    if (immediate) {
      flush();
      return;
    }
    customSaveTimerRef.current = window.setTimeout(flush, 800);
  }, [clientId, useCaseId]);

  useEffect(() => {
    return () => {
      if (customSaveTimerRef.current != null) {
        window.clearTimeout(customSaveTimerRef.current);
      }
    };
  }, []);

  const handleAssumptionEdit = (assumptionId: string, newValue: number) => {
    setActiveScenario('custom');
    setAssumptionGroups(prevGroups => {
      const nextGroups = prevGroups.map(group => ({
        ...group,
        assumptions: group.assumptions.map(assumption =>
          assumption.id === assumptionId
            ? {
                ...assumption,
                custom: newValue,
                changed: (() => {
                  const baseline = assumption.baseRaw ?? assumption.base;
                  return Math.abs(newValue - baseline) > 1e-9 * Math.max(1, Math.abs(baseline));
                })(),
              }
            : assumption
        )
      }));
      persistCustomAssumptions(nextGroups);
      return nextGroups;
    });
  };

  const handleResetAssumption = (assumptionId: string) => {
    setAssumptionGroups(prevGroups => {
      const nextGroups = prevGroups.map(group => ({
        ...group,
        assumptions: group.assumptions.map(assumption =>
          assumption.id === assumptionId
            ? {
                ...assumption,
                custom: assumption.baseRaw ?? assumption.base,
                changed: false,
              }
            : assumption
        )
      }));
      persistCustomAssumptions(nextGroups, true);
      return nextGroups;
    });
  };

  const handleCustomInputBlur = () => {
    if (customSaveTimerRef.current != null) {
      window.clearTimeout(customSaveTimerRef.current);
      customSaveTimerRef.current = null;
    }
    persistCustomAssumptions(assumptionGroups, true);
  };

  const toggleGroupExpanded = (groupId: string) => {
    setAssumptionGroups(prevGroups => prevGroups.map(group => group.id === groupId ? { ...group, expanded: !group.expanded } : group));
  };

  const handleScenarioSelect = (scenarioId: ScenarioId) => {
    setActiveScenario(scenarioId);
  };

  const handleEditSensitivityAssumption = useCallback((assumptionKey?: string) => {
    if (!assumptionKey) return;

    const owningGroup = assumptionGroups.find((group) =>
      group.assumptions.some((assumption) => assumption.id === assumptionKey)
    );
    if (!owningGroup) return;

    setActiveScenario('custom');
    setAssumptionGroups((prevGroups) =>
      prevGroups.map((group) =>
        group.id === owningGroup.id ? { ...group, expanded: true } : group
      )
    );
    setHighlightedAssumptionId(assumptionKey);

    window.setTimeout(() => {
      assumptionsLabRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const input = document.getElementById(`assumption-input-${assumptionKey}`) as HTMLInputElement | null;
      input?.focus();
      input?.select();
    }, 150);

    window.setTimeout(() => setHighlightedAssumptionId(null), 2500);
  }, [assumptionGroups]);

  const handleViewResourceCenter = () => {
    navigate('/ea/activate', {
      state: {
        restore: restoreState,
        focusUseCaseId: useCaseId,
      },
    });
  };

  const handleExportCSV = async () => {
    if (!clientId || !useCaseId || isExportingCsv) return;
    setIsExportingCsv(true);
    setExportCsvError(null);
    try {
      await exportRoiModelCsv(String(clientId), useCaseId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to export ROI CSV';
      setExportCsvError(message);
      console.error('Failed to export ROI CSV:', err);
    } finally {
      setIsExportingCsv(false);
    }
  };

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'External Benchmark': return <Database className="w-3.5 h-3.5 text-[#3b82f6]" />;
      case 'Historical': return <TrendingUp className="w-3.5 h-3.5 text-[#18365E]" />;
      case 'Estimate': return <Lightbulb className="w-3.5 h-3.5 text-[#f59e0b]" />;
      default: return <Info className="w-3.5 h-3.5 text-[#71717a]" />;
    }
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-[#F5F7FA]'}>
      {!embedded && (
        <div className="px-6 pt-3 pb-1"> {/* DMD-1168: tighter wrap */}
          <EAJourneyBar />
        </div>
      )}

      {!embedded && (
      <div className="sticky top-[72px] z-10 bg-white/95 backdrop-blur border-b border-[#e5e7eb] px-6 py-2">
        <div className="max-w-[1280px] mx-auto flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#717182] hover:text-[#18365E] transition-colors shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {backLabel}
          </button>
          <span className="text-[#e5e7eb]" aria-hidden="true">|</span>
          <span className="text-xs font-bold text-[#18365E] truncate min-w-0">
            {rank ? `${rank}. ` : ''}{data.title}
          </span>
          <div className="ml-auto flex items-center gap-1 shrink-0">
            {[
              { key: 'profile' as const, label: 'Decision Brief', icon: FileText, path: `/ea/use-case/${String(useCaseId || '')}/brief` },
              { key: 'rationale' as const, label: 'Scoring Rationale', icon: Target, path: `/ea/use-case/${String(useCaseId || '')}/rationale` },
              { key: 'roi' as const, label: 'ROI Model', icon: LineChart, path: `/ea/use-case/${String(useCaseId || '')}/roi` },
            ].map((view) => {
              const Icon = view.icon;
              const isActive = view.key === 'roi';
              return (
                <button
                  key={view.key}
                  type="button"
                  disabled={isActive}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => {
                    if (isActive) return;
                    const nextState = buildDrilldownNav(drilldownState, {
                      restore: restoreState ?? drilldownState?.restore,
                    });
                    navigate(view.path, { state: nextState });
                  }}
                  className={`flex items-center gap-1.5 px-3.5 py-3 text-xs font-semibold border-b-2 -mb-px transition-colors ${
                    isActive
                      ? 'border-[#FF6E4C] text-[#FF6E4C] cursor-default'
                      : 'border-transparent text-[#717182] hover:text-[#18365E]'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {view.label}
                </button>
              );
            })}
            <span className="hidden md:inline-block w-px h-5 bg-[#e5e7eb] mx-1" aria-hidden="true" />
            <span className="text-[10px] text-[#717182] mr-1.5 hidden md:block">
              Rank {rank || '—'} of {portfolioSize}
            </span>
            <button
              type="button"
              onClick={() => pageTo(prevId)}
              disabled={!prevId}
              aria-label="Previous ranked opportunity"
              className="w-7 h-7 rounded-lg border border-[#e5e7eb] flex items-center justify-center text-[#18365E] disabled:opacity-30 hover:border-[#18365E] transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => pageTo(nextId)}
              disabled={!nextId}
              aria-label="Next ranked opportunity"
              className="w-7 h-7 rounded-lg border border-[#e5e7eb] flex items-center justify-center text-[#18365E] disabled:opacity-30 hover:border-[#18365E] transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      )}

      <main className={embedded ? 'space-y-2' : 'max-w-[1280px] mx-auto px-6 py-3 space-y-2'}> {/* DMD-1168: tighten vertical spacing */}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Failed to load ROI model: {error}
          </div>
        )}

        {showRoiLoader && (
          <div className="rounded-xl border border-[#e5e7eb] bg-white px-6 py-16 flex flex-col items-center justify-center min-h-[480px] gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-[#18365E]" aria-hidden />
            <p className="text-sm font-semibold text-[#18365E]">Loading ROI model…</p>
            <p className="text-xs text-[#717182] text-center max-w-md">
              Fetching scenarios, assumptions, sensitivity analysis, and financial breakdown from the server.
            </p>
          </div>
        )}

        {!showRoiLoader && roiResponse?.data && (
          <>
        {/* Hub already shows the 5 portfolio KPI tiles — ROI tab uses storefront compact header. */}
        <div
          className={`bg-white border border-[#e5e7eb] overflow-hidden ${
            embedded ? 'rounded-t-xl border-b-0' : 'rounded-xl'
          }`}
        >
          <div className={embedded ? 'px-5 py-4' : 'p-6 pb-5'}>
            <div className={`flex ${embedded ? 'items-center justify-between gap-4 flex-wrap' : 'items-start gap-4 flex-wrap'}`}>
              <div className="flex-1 min-w-[220px]">
                {embedded ? (
                  <>
                    <div className="flex items-center gap-3">
                      <Badge className="bg-[#18365E] text-white px-2 py-1">ROI Model</Badge>
                      <h2 className="text-xl font-semibold text-[#18365E] leading-tight">{data.title}</h2>
                    </div>
                    <p className="text-sm text-[#717182] mt-1">
                      Validate assumptions, model scenarios, run sensitivity analysis, and finalize the ROI story.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <Badge className="bg-[#18365E] text-white px-2 py-0.5">ROI Model</Badge>
                      {categoryLabel && (
                        <span className="text-[10px] font-semibold text-[#FF6E4C] uppercase tracking-wide">
                          {categoryLabel}
                        </span>
                      )}
                      {effortTag && <EffortTagPill tag={effortTag} />}
                    </div>
                    <h1 className="text-2xl font-bold text-[#18365E] leading-tight">{data.title}</h1>
                    <p className="text-sm text-[#717182] mt-1">{data.subtitle}</p>
                  </>
                )}
              </div>

              {embedded ? (
                <div className="flex items-center gap-5 shrink-0 flex-wrap">
                  <button
                    type="button"
                    onClick={handleExportCSV}
                    disabled={isExportingCsv}
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-[#d4d4d8] rounded-lg text-sm font-medium text-[#18365E] hover:bg-[#fafafa] hover:border-[#18365E]/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isExportingCsv ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    {isExportingCsv ? 'Exporting…' : 'Export CSV'}
                  </button>
                  <div className="text-right">
                    <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">ROI (Base)</p>
                    <p className="text-2xl font-bold text-[#18365E] tabular-nums leading-tight">
                      {formatHeaderRoiPercent(data.roiBase)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">Payback</p>
                    <p className="text-2xl font-bold text-[#18365E] tabular-nums leading-tight">
                      {selectedScenario?.paybackPeriod != null
                        ? `${selectedScenario.paybackPeriod} mo`
                        : data.paybackPeriod == null
                          ? 'TBD'
                          : `${data.paybackPeriod} mo`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">Net 3-Year Value</p>
                    <p className="text-2xl font-bold text-[#18365E] tabular-nums leading-tight">
                      {formatCurrency(
                        selectedScenario
                          ? selectedScenario.netValue3Year * 1_000_000
                          : (data.netValue3Year ?? 0),
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">Confidence</p>
                    <Badge
                      className={`mt-0.5 ${
                        data.confidence === 'High'
                          ? 'bg-[#18365E]/10 text-[#18365E]'
                          : data.confidence === 'Medium'
                            ? 'bg-[#f59e0b]/20 text-[#f59e0b]'
                            : 'bg-[#ef4444]/20 text-[#ef4444]'
                      }`}
                    >
                      {data.confidence}
                    </Badge>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  <button
                    type="button"
                    onClick={handleShare}
                    className="flex items-center gap-1.5 text-xs font-semibold text-[#18365E] border border-[#e5e7eb] rounded-lg px-3 py-2 hover:border-[#18365E] transition-colors"
                  >
                    <Share2 className="w-3.5 h-3.5" />Share
                  </button>
                  <button
                    type="button"
                    onClick={handleExportCSV}
                    disabled={isExportingCsv}
                    className="flex items-center gap-1.5 text-xs font-semibold text-white bg-[#18365E] rounded-lg px-3 py-2 hover:bg-[#122a4a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isExportingCsv ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    {isExportingCsv ? 'Exporting…' : 'Export CSV'}
                  </button>
                </div>
              )}
            </div>
            {exportCsvError && (
              <p className="mt-2 text-[10px] text-red-600">{exportCsvError}</p>
            )}
          </div>

          {/* Standalone page only — hub already shows portfolio KPIs above the tabs. */}
          {!embedded && (
            <div className="px-6 pb-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
              <StatTile
                variant="dominant"
                label="Annual value"
                value={formatCurrency(selectedScenario?.roi ?? 0)}
                icon={<TrendingUp className="w-5 h-5" />}
              />
              <StatTile
                label="Payback"
                value={selectedScenario?.paybackPeriod != null ? `${selectedScenario.paybackPeriod} mo` : (data.paybackPeriod == null ? 'TBD' : `${data.paybackPeriod} mo`)}
                icon={<Clock className="w-4 h-4" />}
              />
              <StatTile
                label="Net 3-year value"
                value={formatCurrency((selectedScenario?.netValue3Year ?? 0) * 1_000_000)}
                icon={<Award className="w-4 h-4" />}
              />
              <StatTile
                label="ROI (base)"
                value={formatHeaderRoiPercent(data.roiBase)}
                icon={<BarChart3 className="w-4 h-4" />}
              />
              <StatTile
                label="Confidence"
                value={data.confidence}
                icon={<Target className="w-4 h-4" />}
              />
            </div>
          )}
        </div>

        {/* Scenario Performance Overview */}
        <div
          className={`bg-white border border-[#e5e7eb] ${
            embedded ? 'rounded-b-xl border-t-0' : 'rounded-xl'
          }`}
        >
          <div className="px-6 py-4">
            <div className="flex items-center gap-1.5 mb-3">
              <h3 className="text-xs text-[#52525b] uppercase tracking-wider font-bold">Scenario Performance Overview</h3>
              <HelpPopover helpKey="scenario_analysis" iconSize={14} />
            </div>

            {/* Scenario Cards */}
            <div className="grid grid-cols-4 gap-3 mb-4">
              {scenarios.map((scenario) => {
                const isSelected = scenario.id === activeScenario;
                const roiDelta = getDeltaFromBase(scenario.roi, baseScenario?.roi ?? 0);
                const modifiedCount = scenario.modifiedCount;

                return (
                  <button
                    key={scenario.id}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => handleScenarioSelect(scenario.id)}
                    className={`p-3 rounded-lg transition-all duration-200 text-left relative ${isSelected ? 'border-[3px] border-[#18365E] bg-[#18365E]/5 shadow-lg ring-2 ring-[#18365E]/20' : 'border-2 border-[#d4d4d8] bg-white hover:border-[#18365E]/40 hover:shadow-md'
                      }`}
                  >
                    {isSelected && <Badge className="absolute top-2 right-2 text-[8px] px-1.5 py-0 bg-[#18365E] text-white">Active</Badge>}
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="text-xs font-bold text-[#18365E] block mb-0.5">{scenario.name}</span>
                        <span className={`text-[9px] font-medium ${scenario.sublabel === 'User Modified' ? 'text-[#FF6E4C]' : 'text-[#71717a]'}`}>{scenario.sublabel}</span>
                      </div>
                      {modifiedCount != null && modifiedCount > 0 && (
                        <Badge className="text-[9px] px-1.5 py-0 bg-[#FF6E4C] text-white">{modifiedCount} Modified</Badge>
                      )}
                    </div>
                    <div className="space-y-1 mb-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] text-[#71717a] font-semibold">Annual value</span>
                        <div className="flex items-center gap-1">
                          <span className="text-base font-bold text-[#18365E]">{formatCurrency(scenario.roi)}</span>
                          {scenario.id !== 'base' && roiDelta.delta > 0 && (
                            <span className={`text-[9px] ${roiDelta.isUp ? 'text-[#18365E]' : 'text-[#ef4444]'}`}>
                              {roiDelta.isUp ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] text-[#71717a] font-semibold">Payback</span>
                        <span className="text-xs font-semibold text-[#18365E]">{scenario.paybackPeriod} mo</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] text-[#71717a] font-semibold">3-Yr Value</span>
                        <span className="text-xs font-semibold text-[#18365E]">{formatCurrency(scenario.netValue3Year * 1_000_000)}</span>
                      </div>
                    </div>
                    <Badge
                      className={`text-[9px] px-1 py-0 border ${
                        scenario.confidence === 'High'
                          ? 'bg-[#18365E]/10 text-[#18365E] border-[#18365E]/30'
                          : scenario.confidence === 'Medium'
                            ? 'bg-[#f59e0b]/20 text-[#f59e0b] border-[#f59e0b]/30'
                            : 'bg-[#ef4444]/20 text-[#ef4444] border-[#ef4444]/30'
                      }`}
                    >
                      {scenario.confidence}
                    </Badge>
                  </button>
                );
              })}
            </div>

            {/* Scenario Detail Panel */}
            <div className="bg-gradient-to-br from-white to-[#fafafa] border border-[#d4d4d8] rounded-lg p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <h4 className="text-sm font-bold text-[#18365E] flex items-center gap-1.5">
                    <Activity className="w-4 h-4 shrink-0" />
                    Scenario: {selectedScenario?.name ?? '—'}
                  </h4>
                  <p className="text-[10px] text-[#52525b] font-medium mt-0.5">
                    {selectedScenario?.description ?? ''}
                  </p>
                </div>
                {selectedScenario?.confidence && (
                  <Badge
                    className={`shrink-0 px-2 py-0.5 text-[10px] ${
                      selectedScenario.confidence === 'High'
                        ? 'bg-[#18365E] text-white'
                        : selectedScenario.confidence === 'Medium'
                          ? 'bg-[#f59e0b] text-white'
                          : 'bg-[#ef4444] text-white'
                    }`}
                  >
                    {selectedScenario.confidence} Confidence
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4 text-[10px]">
                <div>
                  <h5 className="text-[9px] text-[#52525b] mb-1.5 uppercase tracking-wider font-bold flex items-center gap-1 pb-1.5 border-b border-[#e4e4e7]">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#18365E]" />Value Drivers
                  </h5>
                  <ul className="space-y-1 mt-1.5">
                    {(selectedScenario?.valueDrivers ?? []).length === 0 ? (
                      <li className="text-[#717182]">No value drivers returned for this scenario.</li>
                    ) : (
                      (selectedScenario?.valueDrivers ?? []).map((driver, index) => (
                        <li key={index} className="flex items-start gap-1.5">
                          <span className="text-[#18365E] text-xs flex-shrink-0 mt-0.5">✓</span>
                          <span className="text-[#27272a] font-medium">{driver}</span>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
                <div className="border-l border-[#e4e4e7] pl-4">
                  <h5 className="text-[9px] text-[#52525b] mb-1.5 uppercase tracking-wider font-bold flex items-center gap-1 pb-1.5 border-b border-[#e4e4e7]">
                    <Target className="w-3.5 h-3.5 text-[#18365E]" />Key Assumptions
                  </h5>
                  <ul className="space-y-1 mt-1.5">
                    {(selectedScenario?.keyAssumptions ?? []).map((assumption, index) => (
                      <li key={index} className="flex items-start gap-1.5">
                        <span className="text-[#3b82f6] text-xs flex-shrink-0 mt-0.5">•</span>
                        <span className="text-[#27272a] font-medium">{assumption}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="border-l border-[#e4e4e7] pl-4">
                  <h5 className="text-[9px] text-[#52525b] mb-1.5 uppercase tracking-wider font-bold flex items-center gap-1 pb-1.5 border-b border-[#e4e4e7]">
                    <AlertCircle className="w-3.5 h-3.5 text-[#f59e0b]" />Risks
                  </h5>
                  <ul className="space-y-1 mt-1.5">
                    {(selectedScenario?.risks ?? []).map((risk, index) => (
                      <li key={index} className="flex items-start gap-1.5">
                        <AlertCircle className="w-2.5 h-2.5 text-[#f59e0b] flex-shrink-0 mt-1" />
                        <span className="text-[#27272a] font-medium">{risk}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main 2-Column Layout */}
        <div className="grid grid-cols-3 gap-6">
          {/* LEFT: Assumptions Lab + Waterfall */}
          <div className="col-span-2 space-y-6">
            {/* Assumptions Laboratory */}
            <div ref={assumptionsLabRef} className="bg-white border border-[#d4d4d8] rounded-lg">
              <div className="px-4 py-3 border-b border-[#e4e4e7] bg-[#fafafa]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-xs font-bold text-[#18365E]">Assumptions Laboratory</h3>
                    <HelpPopover helpKey="key_assumptions" iconSize={14} />
                  </div>
                  <p className={`text-[9px] font-medium ${customSaveStatus === 'error' ? 'text-[#ef4444]' : 'text-[#52525b]'}`}>
                    {customSaveStatus === 'saving' ? 'Saving…' : customSaveStatus === 'saved' ? 'Saved' : customSaveStatus === 'error' ? 'Save failed' : 'Edit key drivers'}
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[10px]">
                  <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
                    <tr>
                      <th className="px-3 py-2 text-left text-[9px] text-[#52525b] uppercase tracking-wider font-bold">Assumption</th>
                      <th className="px-2 py-2 text-right text-[9px] text-[#52525b] uppercase tracking-wider font-bold w-14">Cons.</th>
                      <th className="px-2 py-2 text-right text-[9px] text-[#52525b] uppercase tracking-wider font-bold w-14">Base</th>
                      <th className="px-2 py-2 text-right text-[9px] text-[#52525b] uppercase tracking-wider font-bold w-14">Agg.</th>
                      <th className="px-2 py-2 text-right text-[9px] text-[#52525b] uppercase tracking-wider font-bold w-20">Custom</th>
                      <th className="px-2 py-2 text-center text-[9px] text-[#52525b] uppercase tracking-wider font-bold w-14">Conf.</th>
                      <th className="px-2 py-2 text-left text-[9px] text-[#52525b] uppercase tracking-wider font-bold w-24">Source</th>
                      <th className="px-2 py-2 text-center text-[9px] text-[#52525b] uppercase tracking-wider font-bold w-16">Impact</th>
                      <th className="px-2 py-2 w-6"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {assumptionGroups.map((group) => {
                      const GroupIcon = iconMap[group.icon] || DollarSign;
                      return (
                        <React.Fragment key={group.id}>
                          <tr className="bg-[#18365E]/5 cursor-pointer hover:bg-[#18365E]/10 transition-colors" onClick={() => toggleGroupExpanded(group.id)}>
                            <td colSpan={9} className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <GroupIcon className="w-4 h-4 text-[#18365E]" />
                                {group.expanded ? <ChevronDown className="w-3.5 h-3.5 text-[#18365E]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#18365E]" />}
                                <span className="text-[10px] font-bold text-[#18365E]">{group.name}</span>
                                <span className="text-[9px] text-[#71717a] font-medium">({group.assumptions.length})</span>
                              </div>
                            </td>
                          </tr>
                          {group.expanded && group.assumptions.map((assumption, rowIndex) => (
                            <tr
                              key={assumption.id}
                              className={`border-b border-[#e4e4e7] hover:bg-[#18365E]/5 transition-colors ${rowIndex % 2 === 0 ? 'bg-[#fafafa]/50' : 'bg-white'} ${assumption.changed ? 'bg-[#FF6E4C]/5' : ''} ${highlightedAssumptionId === assumption.id ? 'ring-2 ring-inset ring-[#FF6E4C]/50 bg-[#FF6E4C]/10' : ''}`}
                            >
                              <td className="px-3 py-2.5">
                                <div className="flex items-center gap-1.5">
                                  <Info className="w-3.5 h-3.5 text-[#71717a]" />
                                  <span className="text-[#27272a] font-medium">{assumption.name}</span>
                                  {assumption.changed && <div className="w-1.5 h-1.5 rounded-full bg-[#FF6E4C]" />}
                                </div>
                              </td>
                              <td className="px-2 py-2.5 text-right text-[#71717a] font-medium">{formatAssumptionDisplayValue(assumption.conservative)}{assumption.unit}</td>
                              <td className="px-2 py-2.5 text-right text-[#18365E] font-semibold">{formatAssumptionDisplayValue(assumption.base)}{assumption.unit}</td>
                              <td className="px-2 py-2.5 text-right text-[#71717a] font-medium">{formatAssumptionDisplayValue(assumption.aggressive)}{assumption.unit}</td>
                              <td className="px-2 py-2.5">
                                <input
                                  id={`assumption-input-${assumption.id}`}
                                  type="number"
                                  step="0.01"
                                  value={formatAssumptionDisplayValue(assumption.custom)}
                                  onChange={(e) => handleAssumptionEdit(assumption.id, parseFloat(e.target.value) || 0)}
                                  onBlur={handleCustomInputBlur}
                                  className={`w-full px-2 py-1 text-right bg-white border rounded text-[#18365E] font-semibold transition-all ${assumption.changed ? 'border-[#FF6E4C] bg-[#FF6E4C]/5 ring-1 ring-[#FF6E4C]/20' : 'border-[#e4e4e7] hover:border-[#3b82f6] focus:border-[#3b82f6] focus:ring-1 focus:ring-[#3b82f6]/20'
                                    } focus:outline-none`}
                                />
                              </td>
                              <td className="px-2 py-2.5 text-center">
                                <Badge className={`text-[9px] px-1 py-0 ${assumption.confidence === 'High' ? 'bg-[#18365E]/10 text-[#18365E]' : assumption.confidence === 'Medium' ? 'bg-[#f59e0b]/20 text-[#f59e0b]' : 'bg-[#ef4444]/20 text-[#ef4444]'}`}>
                                  {assumption.confidence}
                                </Badge>
                              </td>
                              <td className="px-2 py-2.5">
                                <div className="flex items-center gap-1">
                                  {getSourceIcon(assumption.sourceType)}
                                  <span className="text-[9px] text-[#52525b] font-medium truncate max-w-[80px]">{assumption.source}</span>
                                </div>
                              </td>
                              <td className="px-2 py-2.5">
                                <div className="flex items-center gap-1">
                                  <div className="flex-1 h-1.5 bg-[#e4e4e7] rounded-full overflow-hidden">
                                    <div className={`h-full transition-all ${assumption.impact >= 80 ? 'bg-[#18365E]' : assumption.impact >= 60 ? 'bg-[#f59e0b]' : 'bg-[#71717a]'}`} style={{ width: `${assumption.impact}%` }} />
                                  </div>
                                  <span className="text-[9px] text-[#71717a] w-5 text-right font-medium">{assumption.impact}</span>
                                </div>
                              </td>
                              <td className="px-2 py-2.5 text-center">
                                {assumption.changed && (
                                  <button onClick={() => handleResetAssumption(assumption.id)} className="text-[#71717a] hover:text-[#FF6E4C] transition-colors">
                                    <RefreshCw className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Financial Breakdown Chart */}
            <div className="bg-white border border-[#d4d4d8] rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-xs font-bold text-[#18365E]">Financial Breakdown (Year 1 Impact)</h3>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full text-[#717182] hover:text-[#18365E] hover:bg-[#18365E]/5 transition-colors"
                          aria-label="Help: Financial breakdown"
                        >
                          <HelpCircle className="w-3.5 h-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="bg-[#18365E] text-white border-0 max-w-xs">
                        <p className="text-[10px] font-medium">
                          Year 1 Impact: Benefits equal the selected scenario&apos;s Annual Value; Costs are Year 1 investment.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-[9px] text-[#52525b] font-medium flex items-center gap-1.5">
                  <span>Baseline → Benefits → Costs → Net Value</span>
                </p>
              </div>
              <WaterfallChart financial={selectedFinancial} />
            </div>
          </div>

          {/* RIGHT: Sensitivity + AI Insight */}
          <div className="space-y-6">
            {/* Sensitivity Analysis */}
            <div className="bg-white border border-[#d4d4d8] rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-xs font-bold text-[#18365E]">Sensitivity Analysis</h3>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full text-[#717182] hover:text-[#18365E] hover:bg-[#18365E]/5 transition-colors"
                          aria-label="Help: Sensitivity analysis"
                        >
                          <HelpCircle className="w-3.5 h-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="bg-[#18365E] text-white border-0 max-w-xs">
                        <p className="text-[10px] font-medium">
                          Ranking shows which assumptions have the greatest impact on net value when varied by ±20%.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              <p className="text-[9px] text-[#52525b] mb-3 font-medium">Impact of ±20% variance (ranked by magnitude)</p>
              <div className="space-y-3">
                {mappedSensitivityData.map((item) => {
                  const isPrimary = item.rank <= 2;
                  const isSecondary = item.rank >= 3 && item.rank <= 5;
                  return (
                    <TooltipProvider key={item.rank}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                {isPrimary && <Badge className="text-[10px] px-2 py-0.5 bg-[#FF6E4C] text-white font-bold">#{item.rank}</Badge>}
                                {isSecondary && <Badge className="text-[9px] px-1.5 py-0 bg-[#18365E] text-white font-semibold">#{item.rank}</Badge>}
                                {item.rank > 5 && <Badge className="text-[8px] px-1 py-0 bg-slate-400/40 text-slate-600">#{item.rank}</Badge>}
                                <span className={`text-[10px] text-[#27272a] ${isPrimary ? 'font-bold' : 'font-medium'}`}>{item.name}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-[9px] text-[#52525b] font-medium">{item.label}</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditSensitivityAssumption(item.assumptionKey);
                                  }}
                                  className="text-[#71717a] hover:text-[#FF6E4C] transition-colors"
                                  aria-label={`Edit ${item.name} assumption`}
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            <div className={`${isPrimary ? 'h-5' : 'h-4'} bg-[#fafafa] rounded overflow-hidden relative border ${isPrimary ? 'border-[#FF6E4C]/30' : 'border-[#e4e4e7]'}`}>
                              <div className="absolute inset-0 flex">
                                <div className={`${isPrimary ? 'bg-[#dc2626]' : 'bg-[#ef4444]/70'} h-full`} style={{ width: `${50 - (Math.abs(item.negative) / (Math.abs(item.negative) + item.positive)) * 50}%` }} />
                                <div className="w-px bg-[#71717a]" />
                                <div className={`${isPrimary ? 'bg-[#18365E]' : 'bg-[#18365E]/70'} h-full`} style={{ width: `${(item.positive / (Math.abs(item.negative) + item.positive)) * 50}%` }} />
                              </div>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="bg-[#18365E] text-white border-0 max-w-sm">
                          <p className="text-[10px] font-semibold mb-1">
                            {item.rank === 1 ? 'Strongest impact on Net Value' : `#${item.rank} impact on Net Value`}
                          </p>
                          <p className="text-[9px] text-slate-300">{item.impactExplanation}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
              <div className="mt-3 pt-3 border-t border-[#e4e4e7]">
                <div className="flex items-center gap-3 text-[9px]">
                  <div className="flex items-center gap-1"><div className="w-3 h-1.5 bg-[#ef4444]/80 rounded" /><span className="text-[#52525b] font-medium">Downside</span></div>
                  <div className="flex items-center gap-1"><div className="w-3 h-1.5 bg-[#18365E]/80 rounded" /><span className="text-[#52525b] font-medium">Upside</span></div>
                </div>
              </div>
            </div>

            {/* AI Insight Panel */}
            <div className="relative p-[2px] rounded-lg bg-gradient-to-r from-[#18365E]/20 via-[#FF6E4C]/20 to-[#18365E]/20">
              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-[#FF6E4C]" />
                  <div className="flex-1">
                    <h4 className="text-xs font-bold text-[#18365E] flex items-center gap-1">
                      AI Insight
                      <Badge className="text-[8px] px-1 py-0 bg-[#FF6E4C]/20 text-[#FF6E4C] border border-[#FF6E4C]/30">AI-Generated</Badge>
                    </h4>
                  </div>
                </div>
                <p className="text-[10px] text-[#27272a] mb-3 font-medium leading-relaxed">
                  {data.aiInsight || 'AI-generated insight will appear here when the model returns narrative text.'}
                </p>
                <button
                  type="button"
                  onClick={handleViewResourceCenter}
                  className="text-[10px] text-[#18365E] hover:text-[#FF6E4C] transition-colors flex items-center gap-0.5 font-semibold"
                >
                  <ArrowUpRight className="w-3 h-3" />View Resource Center
                </button>
              </div>
            </div>
          </div>
        </div>
          </>
        )}
      </main>
    </div>
  );
};

export default EAROIModelPage;


